import React, { useState } from 'react';
import {
  FilePlus,
  Send,
  UserCheck,
  ShieldCheck,
  Clock,
  CheckCircle,
  AlertTriangle,
  History,
  Code,
  Layers,
  Sparkles,
} from 'lucide-react';
import { AuthorProfile, ContentEntry, RiskLevel, WorkflowStatus } from '../../types';
import { seoCmsEngine } from '../../services/seoCmsEngine';

export const EditorialCmsHub: React.FC = () => {
  const [guides, setGuides] = useState<ContentEntry[]>(seoCmsEngine.getGuides());
  const [authors] = useState<AuthorProfile[]>(seoCmsEngine.getAuthors());
  const [selectedGuideId, setSelectedGuideId] = useState<string>(guides[0]?.id || '');
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);

  // New Draft Form state
  const [newTitle, setNewTitle] = useState('');
  const [newSummary, setNewSummary] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newAuthorId, setNewAuthorId] = useState(authors[0]?.id || '');
  const [newTopicCluster, setNewTopicCluster] = useState('Salary & Income Tax');
  const [newRiskLevel, setNewRiskLevel] = useState<RiskLevel>('MODERATE');

  // Correction Form state
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [corrBy, setCorrBy] = useState('Sarah Jenkins, CPA');
  const [corrPrev, setCorrPrev] = useState('');
  const [corrNext, setCorrNext] = useState('');
  const [corrReason, setCorrReason] = useState('');

  const selectedGuide = guides.find((g) => g.id === selectedGuideId);
  const revisions = selectedGuideId ? seoCmsEngine.getRevisionsForContent(selectedGuideId) : [];

  const handleCreateDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle || !newSummary || !newBody) return;

    const created = seoCmsEngine.createGuideDraft(
      newTitle,
      newSummary,
      newBody,
      newAuthorId,
      newTopicCluster,
      newRiskLevel
    );

    setGuides([...seoCmsEngine.getGuides()]);
    setSelectedGuideId(created.id);
    setIsCreatingDraft(false);
    setNewTitle('');
    setNewSummary('');
    setNewBody('');
  };

  const handleStatusChange = (newStatus: WorkflowStatus) => {
    if (!selectedGuide) return;
    const updated = seoCmsEngine.updateWorkflowStatus(
      selectedGuide.id,
      newStatus,
      'Editorial Manager',
      `Workflow status updated to ${newStatus}`
    );
    setGuides([...seoCmsEngine.getGuides()]);
  };

  const handleAddCorrection = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGuide || !corrReason) return;

    seoCmsEngine.addGuideCorrection(selectedGuide.id, corrBy, corrPrev, corrNext, corrReason);
    setGuides([...seoCmsEngine.getGuides()]);
    setShowCorrectionForm(false);
    setCorrReason('');
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
            <Layers className="w-3.5 h-3.5" />
            YamooPay Editorial Content Management System (CMS)
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            Financial CMS & Editorial Governance Hub
          </h1>
          <p className="text-slate-300 text-sm leading-relaxed">
            Manage multi-locale articles, assign verified CPA/CFA reviewers, track revision histories, and enforce YMYL risk level workflows.
          </p>
        </div>

        <button
          onClick={() => setIsCreatingDraft(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-xl transition-all shadow-md shrink-0"
        >
          <FilePlus className="w-4 h-4" />
          Create New Draft
        </button>
      </div>

      {/* Main Grid: Guide Pipeline list + Selected Guide CMS Workstation */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Article Pipeline (4 Cols) */}
        <div className="lg:col-span-4 space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Editorial Pipeline ({guides.length})
            </h2>
          </div>

          <div className="space-y-3">
            {guides.map((guide) => {
              const isSelected = guide.id === selectedGuideId;
              const author = authors.find((a) => a.id === guide.authorId);

              return (
                <div
                  key={guide.id}
                  onClick={() => setSelectedGuideId(guide.id)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-emerald-50/60 border-emerald-500 ring-1 ring-emerald-500/50 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span
                      className={`px-2 py-0.5 rounded font-bold text-[10px] uppercase ${
                        guide.status === 'PUBLISHED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : guide.status === 'DRAFT'
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {guide.status}
                    </span>
                    <span className="text-slate-400 text-[11px] font-mono">{guide.locale.toUpperCase()}</span>
                  </div>

                  <h3 className="font-bold text-slate-900 text-sm line-clamp-2 mb-2">{guide.title}</h3>

                  <div className="flex items-center justify-between text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                    <span>{author ? author.name : 'Author Unassigned'}</span>
                    <span>{guide.updatedAt.split('T')[0]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: CMS Workstation (8 Cols) */}
        <div className="lg:col-span-8">
          {selectedGuide ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
              {/* Status Pipeline Step Actions */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-slate-500" />
                    Workflow Pipeline Stage
                  </span>
                  <span className="text-xs font-bold text-emerald-700 bg-white px-2.5 py-1 rounded border border-slate-200 shadow-2xs">
                    Current: {selectedGuide.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {(['DRAFT', 'FACT_CHECK', 'FINANCIAL_REVIEW', 'APPROVED', 'PUBLISHED', 'ARCHIVED'] as WorkflowStatus[]).map(
                    (st) => (
                      <button
                        key={st}
                        onClick={() => handleStatusChange(st)}
                        disabled={selectedGuide.status === st}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          selectedGuide.status === st
                            ? 'bg-slate-900 text-white ring-2 ring-slate-900/20'
                            : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {st}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Title & Metadata Inputs */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Article Headline
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={selectedGuide.title}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 text-base"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                  <div>
                    <label className="block font-bold text-slate-500 mb-1">Topic Cluster</label>
                    <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-800">
                      {selectedGuide.topicCluster}
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-500 mb-1">YMYL Risk Level</label>
                    <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg font-bold text-amber-800 flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {selectedGuide.riskLevel}
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-500 mb-1">Assigned Author</label>
                    <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-800 flex items-center gap-1">
                      <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                      {authors.find((a) => a.id === selectedGuide.authorId)?.name || 'Editorial Board'}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Executive Summary / Search Description
                  </label>
                  <textarea
                    readOnly
                    rows={2}
                    value={selectedGuide.summary}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 leading-relaxed"
                  />
                </div>
              </div>

              {/* Article Content Preview */}
              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Markdown Content Body
                </label>
                <div className="p-4 bg-slate-900 text-slate-100 rounded-xl font-mono text-xs overflow-x-auto max-h-64 border border-slate-800 leading-relaxed whitespace-pre-wrap">
                  {selectedGuide.bodyMarkdown}
                </div>
              </div>

              {/* Revision History & Corrections Log */}
              <div className="border-t border-slate-200 pt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <History className="w-4 h-4 text-slate-600" />
                    Immutable Revision History ({revisions.length})
                  </h3>

                  <button
                    onClick={() => setShowCorrectionForm(!showCorrectionForm)}
                    className="text-xs font-bold text-amber-700 hover:text-amber-800 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200"
                  >
                    + Add Correction Note
                  </button>
                </div>

                {/* Correction Form Modal/Inline */}
                {showCorrectionForm && (
                  <form onSubmit={handleAddCorrection} className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3 text-xs">
                    <div className="font-bold text-amber-900 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Log Visible Financial Correction
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Previous incorrect value"
                        value={corrPrev}
                        onChange={(e) => setCorrPrev(e.target.value)}
                        className="px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-xs"
                      />
                      <input
                        type="text"
                        placeholder="New verified value"
                        value={corrNext}
                        onChange={(e) => setCorrNext(e.target.value)}
                        className="px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-xs"
                      />
                    </div>
                    <textarea
                      placeholder="Reason for correction (e.g. Updated 2026 IRS withholding table revision)..."
                      value={corrReason}
                      onChange={(e) => setCorrReason(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-xs"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowCorrectionForm(false)}
                        className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-xs font-semibold"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-3 py-1 bg-amber-600 text-white rounded text-xs font-bold"
                      >
                        Save Correction Log
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {revisions.map((rev) => (
                    <div
                      key={rev.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs"
                    >
                      <div className="space-y-0.5">
                        <div className="font-semibold text-slate-800">
                          Rev #{rev.revisionNumber}: {rev.changeSummary}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          By <span className="font-bold">{rev.changedBy}</span> • Status: {rev.workflowStatus}
                        </div>
                      </div>
                      <span className="text-[11px] text-slate-400">{rev.changedAt.split('T')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
              Select an article on the left to edit or manage editorial workflows.
            </div>
          )}
        </div>
      </div>

      {/* Create Draft Modal */}
      {isCreatingDraft && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl border border-slate-200 shadow-2xl p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-lg text-slate-900">Create New Financial Article Draft</h3>
              </div>
              <button
                onClick={() => setIsCreatingDraft(false)}
                className="text-slate-400 hover:text-slate-600 text-xs font-bold px-2 py-1 rounded bg-slate-100"
              >
                Close
              </button>
            </div>

            <form onSubmit={handleCreateDraft} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Article Headline / Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., UK PAYE Tax & National Insurance Take-Home Pay Guide 2026"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Topic Cluster</label>
                  <select
                    value={newTopicCluster}
                    onChange={(e) => setNewTopicCluster(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                  >
                    <option value="Salary & Income Tax">Salary & Income Tax</option>
                    <option value="International Transfers">International Transfers</option>
                    <option value="Loans & Mortgages">Loans & Mortgages</option>
                    <option value="Currency Exchange">Currency Exchange</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">YMYL Risk Level</label>
                  <select
                    value={newRiskLevel}
                    onChange={(e) => setNewRiskLevel(e.target.value as RiskLevel)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                  >
                    <option value="LOW">LOW</option>
                    <option value="MODERATE">MODERATE</option>
                    <option value="HIGH">HIGH</option>
                    <option value="CRITICAL_FINANCIAL">CRITICAL_FINANCIAL</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Author Assignment</label>
                  <select
                    value={newAuthorId}
                    onChange={(e) => setNewAuthorId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                  >
                    {authors.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.credentials[0]})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Executive Summary</label>
                <textarea
                  rows={2}
                  required
                  placeholder="Brief summary of regulatory rules, formulas, and user value..."
                  value={newSummary}
                  onChange={(e) => setNewSummary(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Markdown Body</label>
                <textarea
                  rows={8}
                  required
                  placeholder="# Heading&#10;&#10;Detailed financial breakdown..."
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-xs"
                />
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsCreatingDraft(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-semibold text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow-sm"
                >
                  Save Draft
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
